import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Helmet } from 'react-helmet-async';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import supabase from '../supabase/supabase';
import { useCart } from '../contexts/CartContext';
import CartSidebar from '../components/CartSidebar';

const { FiArrowLeft, FiCheckCircle, FiZap, FiShield, FiShoppingCart, FiClock, FiFileText } = FiIcons;

const AssessmentLanding = () => {
  const { slug } = useParams();
  const [assessment, setAssessment] = useState(null);
  const [loading, setLoading] = useState(true);
  const [purchaseType, setPurchaseType] = useState('ai');
  const [isCartOpen, setIsCartOpen] = useState(false);
  const { addToCart, cart } = useCart();

  useEffect(() => {
    const fetchAssessment = async () => {
      const { data, error } = await supabase
        .from('assessments_1773012000000')
        .select('*')
        .eq('slug', slug)
        .single();
      
      if (!error) setAssessment(data);
      setLoading(false);
    };
    fetchAssessment();
  }, [slug]);

  if (loading) return <div className="p-20 text-center italic text-gray-400">Loading assessment details...</div>;
  if (!assessment) return <div className="p-20 text-center font-bold">Assessment not found</div>;

  const isInCart = cart.some(item => item.id === assessment.id && item.purchaseType === purchaseType);

  return (
    <div className="min-h-screen bg-white">
      <Helmet>
        <title>{assessment.title} | Business Assessment | Cansulta</title>
        <meta name="description" content={assessment.description} />
      </Helmet>

      <div className="max-w-7xl mx-auto px-4 py-8">
        <Link to="/assessments" className="inline-flex items-center text-gray-500 hover:text-blue-600 transition-colors mb-12 font-bold text-sm">
          <SafeIcon icon={FiArrowLeft} className="mr-2" /> Back to Catalog
        </Link>

        <div className="grid lg:grid-cols-2 gap-16 items-start">
          <motion.div initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }}>
            <div className={`inline-flex items-center gap-2 px-4 py-2 rounded-full text-xs font-bold uppercase tracking-widest mb-6 ${
              assessment.complexity === 'basic' ? 'bg-green-50 text-green-600' : 'bg-orange-50 text-orange-600'
            }`}>
              <SafeIcon icon={FiZap} /> {assessment.complexity} Level
            </div>
            <h1 className="text-4xl md:text-6xl font-black text-gray-900 mb-8 leading-tight font-ubuntu">
              {assessment.title}
            </h1>
            <p className="text-xl text-gray-600 leading-relaxed mb-10">
              {assessment.full_description || assessment.description}
            </p>

            <div className="grid sm:grid-cols-2 gap-8 mb-12">
              <div className="flex gap-4">
                <div className="w-12 h-12 rounded-2xl bg-blue-50 flex items-center justify-center text-blue-600 flex-shrink-0">
                  <SafeIcon icon={FiClock} className="w-6 h-6" />
                </div>
                <div>
                  <p className="text-xs font-bold text-gray-400 uppercase">Duration</p>
                  <p className="font-bold text-gray-900">{assessment.duration}</p>
                </div>
              </div>
              <div className="flex gap-4">
                <div className="w-12 h-12 rounded-2xl bg-purple-50 flex items-center justify-center text-purple-600 flex-shrink-0">
                  <SafeIcon icon={FiFileText} className="w-6 h-6" />
                </div>
                <div>
                  <p className="text-xs font-bold text-gray-400 uppercase">Questions</p>
                  <p className="font-bold text-gray-900">{assessment.questions} Targeted</p>
                </div>
              </div>
            </div>

            <div className="space-y-4">
              <h3 className="text-lg font-bold text-gray-900">What you'll get:</h3>
              <div className="grid grid-cols-1 gap-3">
                {assessment.benefits?.map((b, i) => (
                  <div key={i} className="flex items-center gap-3 text-gray-600">
                    <SafeIcon icon={FiCheckCircle} className="text-green-500 w-5 h-5 flex-shrink-0" />
                    <span className="text-sm font-medium">{b}</span>
                  </div>
                ))}
              </div>
            </div>
          </motion.div>

          <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} className="lg:sticky lg:top-24">
            <div className="bg-white rounded-[2rem] border-4 border-gray-50 shadow-2xl p-8 md:p-10">
              <h3 className="text-xl font-bold text-gray-900 mb-8 font-ubuntu">Choose your analysis depth</h3>
              <div className="space-y-4 mb-10">
                <button 
                  onClick={() => setPurchaseType('ai')}
                  className={`w-full p-6 rounded-2xl border-2 transition-all text-left flex items-center gap-4 ${
                    purchaseType === 'ai' ? 'border-blue-600 bg-blue-50/50' : 'border-gray-100 hover:border-gray-200'
                  }`}
                >
                  <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${purchaseType === 'ai' ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-400'}`}>
                    <SafeIcon icon={FiZap} className="w-6 h-6" />
                  </div>
                  <div className="flex-1">
                    <p className="font-bold text-gray-900">AI Analysis</p>
                    <p className="text-xs text-gray-500">Instant results & recommendations</p>
                  </div>
                  <p className="text-2xl font-black text-gray-900">${assessment.price_ai}</p>
                </button>

                <button 
                  onClick={() => setPurchaseType('expert')}
                  className={`w-full p-6 rounded-2xl border-2 transition-all text-left flex items-center gap-4 ${
                    purchaseType === 'expert' ? 'border-purple-600 bg-purple-50/50' : 'border-gray-100 hover:border-gray-200'
                  }`}
                >
                  <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${purchaseType === 'expert' ? 'bg-purple-600 text-white' : 'bg-gray-100 text-gray-400'}`}>
                    <SafeIcon icon={FiShield} className="w-6 h-6" />
                  </div>
                  <div className="flex-1">
                    <p className="font-bold text-gray-900">AI + Expert Review</p>
                    <p className="text-xs text-gray-500">Includes 30-min strategy call</p>
                  </div>
                  <p className="text-2xl font-black text-gray-900">${assessment.price_expert}</p>
                </button>
              </div>

              <div className="space-y-4">
                <button 
                  onClick={() => {
                    // Adapt DB object to component expectation
                    const compatAssessment = {
                      ...assessment,
                      priceAI: assessment.price_ai,
                      priceExpert: assessment.price_expert
                    };
                    addToCart(compatAssessment, purchaseType);
                    setIsCartOpen(true);
                  }}
                  className="w-full py-5 bg-blue-600 text-white rounded-2xl font-bold text-lg hover:bg-blue-700 shadow-xl shadow-blue-100 transition-all flex items-center justify-center gap-3"
                >
                  <SafeIcon icon={FiShoppingCart} />
                  {isInCart ? 'In Cart' : 'Add to Cart'}
                </button>
                <p className="text-center text-xs text-gray-400 font-medium"> Secure 256-bit encrypted checkout </p>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
      <CartSidebar isOpen={isCartOpen} onClose={() => setIsCartOpen(false)} />
    </div>
  );
};

export default AssessmentLanding;